import type {DataUploadClassOption} from 'common/helpers/dataUpload'
import {Check, ChevronDown, ChevronUp, Users} from 'lucide-react'
import React, {FC, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'

/** One class name the file wrote that matches several classes of the school. */
export interface StudentClassQuestion {
  id: string
  /** The class as the file writes it ("Grade 1 A"). */
  written: string
  /** The students who have it. */
  labels: string[]
  options: DataUploadClassOption[]
  /** The chosen `classId`, if any. */
  picked?: number
}

interface Props {
  show: boolean
  questions: readonly StudentClassQuestion[]
  /** The full label of a class ("Grade 1-A · BISE Punjab · Modal Town"), shown on its choice. */
  optionLabel: (option: DataUploadClassOption) => string
  onAnswer: (questionId: string, option: DataUploadClassOption) => void
  onClose: () => void
}

/** The board of a class, as its label shows it. */
const boardOf = (option: DataUploadClassOption) =>
  (option.curriculumShortCode || option.curriculum).trim()

/**
 * Ways to tell a question's classes apart, tried in order: the board alone ("FBISE"), the
 * branch alone ("Model Town"), then both ("FBISE · Model Town").
 */
const GROUPINGS: Array<(option: DataUploadClassOption) => string> = [
  (option) => boardOf(option),
  (option) => option.branch.trim(),
  (option) => [boardOf(option), option.branch.trim()].filter(Boolean).join(' · '),
]

/**
 * "Same for all": the first grouping under which every question has exactly one class per
 * group, and the groups every question has. Picking one answers every row with its class in
 * that group ("FBISE" -> each class's FBISE one). Nothing when there is only one question.
 */
const sharedGroups = (
  questions: readonly StudentClassQuestion[]
): {groupOf: (option: DataUploadClassOption) => string; groups: string[]} | null => {
  if (questions.length < 2) return null
  for (const groupOf of GROUPINGS) {
    const perQuestion = questions.map((question) => {
      const groups = question.options.map(groupOf)
      return groups.every(Boolean) && new Set(groups).size === groups.length
        ? new Set(groups)
        : null
    })
    if (perQuestion.some((set) => !set)) continue
    const [first, ...rest] = perQuestion as Set<string>[]
    const shared = Array.from(first).filter((group) => rest.every((set) => set.has(group)))
    if (shared.length > 1) return {groupOf, groups: shared}
  }
  return null
}

/** Names listed before "+N more" once a question's names are opened. */
const MAX_NAMES = 30

/**
 * "Which class does the file mean?" — one row per class name the file wrote that matches several
 * classes (two branches set up from one class list both have "Grade 1-A"). A pick fills every row
 * that wrote it, by class id.
 *
 * Each row is the name as written, how many students have it (their names folded away), and one
 * button per class with its full label (class · curriculum · branch). "Same for all" answers
 * every row at once, by board, branch or both — whichever tells each row's classes apart.
 */
const StudentClassQuestionsModal: FC<Props> = ({
  show,
  questions,
  optionLabel,
  onAnswer,
  onClose,
}) => {
  const intl = useIntl()
  /** Questions whose student names are open. */
  const [openNames, setOpenNames] = useState<Record<string, boolean>>({})

  const answered = questions.filter((question) => question.picked !== undefined).length
  const bulk = sharedGroups(questions)
  /** The group every question is answered with, when they all agree. */
  const bulkPicked = bulk?.groups.find((group) =>
    questions.every((question) => {
      const option = question.options.find((entry) => entry.classId === question.picked)
      return option !== undefined && bulk.groupOf(option) === group
    })
  )

  const applyGroup = (group: string) =>
    questions.forEach((question) => {
      const option = question.options.find((entry) => bulk?.groupOf(entry) === group)
      if (option && option.classId !== question.picked) onAnswer(question.id, option)
    })

  return (
    <Modal show={show} onHide={onClose} centered size='lg' scrollable>
      <Modal.Header closeButton className='border-0 pb-0 align-items-start'>
        <div>
          <Modal.Title className='fs-3'>
            {intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_QUESTIONS_TITLE',
            })}
          </Modal.Title>
          <p className='sheet-assign-hint mt-2 mb-0'>
            {intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_QUESTIONS_HINT',
            })}
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className='pt-4'>
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4'>
          <span
            className={`badge ${answered === questions.length ? 'badge-light-primary' : 'badge-light'} fs-8 fw-semibold`}
            role='status'
          >
            {intl.formatMessage(
              {id: 'DATA_UPLOAD.SHEETS.PROGRESS'},
              {answered, total: questions.length}
            )}
          </span>
          {bulk && (
            <div className='class-question-bulk d-flex flex-wrap align-items-center gap-2'>
              <span className='sheet-assign-label mb-0'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_QUESTIONS_SAME_FOR_ALL',
                })}
              </span>
              {bulk.groups.map((group) => (
                <button
                  key={group}
                  type='button'
                  className={`class-question-choice${bulkPicked === group ? ' is-picked' : ''}`}
                  aria-pressed={bulkPicked === group}
                  onClick={() => applyGroup(group)}
                >
                  {bulkPicked === group && <Check size={14} aria-hidden />}
                  {group}
                </button>
              ))}
            </div>
          )}
        </div>

        {questions.map((question) => {
          const namesOpen = Boolean(openNames[question.id])
          const picked = question.picked !== undefined
          return (
            <div
              key={question.id}
              className={`sheet-assign-card class-question-card${picked ? ' is-answered' : ''}`}
            >
              <div className='d-flex flex-wrap align-items-center justify-content-between gap-3'>
                <div className='d-flex flex-wrap align-items-center gap-2' style={{minWidth: 0}}>
                  <span className='sheet-assign-name'>{question.written}</span>
                  <button
                    type='button'
                    className='class-question-count'
                    aria-expanded={namesOpen}
                    onClick={() =>
                      setOpenNames((prev) => ({...prev, [question.id]: !prev[question.id]}))
                    }
                  >
                    <Users size={13} aria-hidden />
                    {intl.formatMessage(
                      {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.STUDENTS.CLASS_QUESTION_STUDENTS'},
                      {count: question.labels.length}
                    )}
                    {namesOpen ? (
                      <ChevronUp size={13} aria-hidden />
                    ) : (
                      <ChevronDown size={13} aria-hidden />
                    )}
                  </button>
                </div>

                <div
                  className='d-flex flex-wrap gap-2'
                  role='radiogroup'
                  aria-label={question.written}
                >
                  {question.options.map((option) => {
                    const chosen = question.picked === option.classId
                    return (
                      <button
                        key={option.classId}
                        type='button'
                        role='radio'
                        aria-checked={chosen}
                        className={`class-question-choice${chosen ? ' is-picked' : ''}`}
                        onClick={() => onAnswer(question.id, option)}
                      >
                        {chosen && <Check size={14} aria-hidden />}
                        {optionLabel(option)}
                      </button>
                    )
                  })}
                </div>
              </div>

              {namesOpen && (
                <div className='class-question-names'>
                  {question.labels.slice(0, MAX_NAMES).join(', ')}
                  {question.labels.length > MAX_NAMES &&
                    ` ${intl.formatMessage(
                      {id: 'DATA_UPLOAD.CONVERT.MORE_ROWS'},
                      {count: question.labels.length - MAX_NAMES}
                    )}`}
                </div>
              )}
            </div>
          )
        })}
      </Modal.Body>

      <Modal.Footer className='border-0'>
        <button type='button' className='btn btn-primary' onClick={onClose}>
          {intl.formatMessage({id: 'GENERAL.DONE'})}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default StudentClassQuestionsModal
